import { useSyncExternalStore } from 'react';
import create, { StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';

/** Diverge needs at least two products to have something to compare. */
export const MIN_COMPARISON_ITEMS = 2;
/** More than three columns stops being readable, and bloats the chat trigger string. */
export const MAX_COMPARISON_ITEMS = 3;

export type ComparisonItem = {
    /** Product id. Diverge matches on this, so names alone are not enough. */
    id: string;
    name: string;
    url: string;
    brand?: string;
    image?: { src: string; alt: string };
    /**
     * A listing the customer can pick more products from: the listing a card was added from,
     * or the product's own category breadcrumb link when added from a product page. Read by
     * the "Vælg op til N mere" CTA, which only acts while a product page is being viewed -
     * on a listing there is already a grid in front of the customer.
     */
    categoryUrl?: string;
};

export type ProductComparisonState = {
    items: ComparisonItem[];
    isCollapsed: boolean;
    /**
     * True right after the pick that completes the comparison to `MAX_COMPARISON_ITEMS` -
     * deliberately not persisted, so it only forces the tray open for the rest of this
     * session and a refresh instead falls back to the persisted `isCollapsed`.
     */
    justReachedMax: boolean;
    toggle(item: ComparisonItem): void;
    remove(id: string): void;
    clear(): void;
    setIsCollapsed(isCollapsed: boolean): void;
};

type ProductComparisonPersist = (
    config: StateCreator<ProductComparisonState>,
    options: PersistOptions<
        ProductComparisonState,
        Pick<ProductComparisonState, 'items' | 'isCollapsed'>
    >,
) => StateCreator<ProductComparisonState>;

/**
 * The comparison follows the customer across pages until they clear it, so the selection
 * outlives client-side navigation and a reload. `isCollapsed` is persisted too, so a mobile
 * refresh keeps the tray as a pill instead of re-expanding over the page - `ProductComparisonTray`
 * forces it back open on desktop, where the tray is small enough to always leave expanded.
 */
export const useProductComparisonStore = create<ProductComparisonState>(
    (persist as ProductComparisonPersist)(
        (set, get) => ({
            items: [],
            isCollapsed: false,
            justReachedMax: false,
            toggle: (item) => {
                const { items } = get();

                if (items.some(({ id }) => id === item.id)) {
                    set({
                        items: items.filter(({ id }) => id !== item.id),
                        justReachedMax: false,
                    });
                    return;
                }

                if (items.length >= MAX_COMPARISON_ITEMS) return;

                const nextItems = [...items, item];

                // Adding re-opens the tray, so the customer sees what they just picked.
                set({
                    items: nextItems,
                    isCollapsed: false,
                    justReachedMax: nextItems.length >= MAX_COMPARISON_ITEMS,
                });
            },
            remove: (id) =>
                set({
                    items: get().items.filter((item) => item.id !== id),
                    justReachedMax: false,
                }),
            clear: () => set({ items: [], isCollapsed: false, justReachedMax: false }),
            setIsCollapsed: (isCollapsed) => set({ isCollapsed, justReachedMax: false }),
        }),
        {
            /**
             * Bump the suffix whenever `ComparisonItem` gains or changes a field, so a
             * selection stored by an older build is left behind rather than restored.
             *
             * A restored selection is trusted wholesale, so items written by an older shape
             * arrive missing whatever was added since - which has already cost us a crash (a
             * slot rendering `<Link href={undefined}>` took the whole page down through the
             * layout error boundary) and a CTA silently stuck disabled on a missing
             * `categoryUrl`. The customer loses one in-progress comparison the first time
             * they load a newer build, instead of meeting a tray that half works.
             *
             * A new key rather than persist's `version`/`migrate`, whose types require the
             * migration to return the store's actions as well as its data - which it has no
             * way to build. The abandoned key is session-scoped and goes when the tab does.
             */
            name: 'product-comparison-v2',
            getStorage: () => sessionStorage,
            partialize: ({ items, isCollapsed }) => ({ items, isCollapsed }),
        },
    ),
);

/**
 * The store is restored from session storage as soon as it is imported, so on a page the
 * server rendered with an empty selection the very first client render would already disagree
 * with the markup and break hydration. Anything derived from the persisted selection therefore
 * reports its server value until after mount.
 */
const neverChanges = () => () => undefined;

const useHasMounted = () =>
    // React renders the server snapshot while hydrating and the client one from then on,
    // which is exactly the "false until mounted" this needs - and unlike a state-in-effect
    // flag it cannot render a frame that disagrees with the markup.
    useSyncExternalStore(
        neverChanges,
        () => true,
        () => false,
    );

/**
 * Narrow selectors, so a product card re-renders when its own selection changes and not
 * every time any other card is toggled.
 */
export const useIsInComparison = (id: string) => {
    const hasMounted = useHasMounted();
    const isInComparison = useProductComparisonStore((state) =>
        state.items.some((item) => item.id === id),
    );

    return hasMounted && isInComparison;
};

export const useHasComparisonItems = () => {
    const hasMounted = useHasMounted();
    const hasItems = useProductComparisonStore((state) => state.items.length > 0);

    return hasMounted && hasItems;
};

export const useIsComparisonFull = () => {
    const hasMounted = useHasMounted();
    const isFull = useProductComparisonStore((state) => state.items.length >= MAX_COMPARISON_ITEMS);

    return hasMounted && isFull;
};
